import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X, Check, XCircle } from 'lucide-react';
import { getTeamLeaveCalendar, postManagerApproveLeave, postManagerDeclineLeave } from "api/apiHelper";
import { toast } from "react-toastify";

// Types
interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  designation: string;
  email: string;
  phone: string;
  photo: string | null;
}

interface LeaveType {
  id: number;
  description: string;
  is_paid: boolean;
}

interface Approver {
  id: number;
  full_name: string;
}

interface Leave {
  id: number;
  start_date: string;
  end_date: string;
  working_days: number;
  leave_status: 'A' | 'MA' | 'P' | 'D' | 'MD';
  leave_status_display: string;
  leave_type: LeaveType;
  reason: string;
  comments: string;
  requested_at: string | null;
  employee: Employee;
  approvals: {
    manager_approved_by: Approver | null;
    approved_by: Approver | null;
  };
}

interface CalendarData {
  manager: {
    id: number;
    full_name: string;
    designation: string;
  };
  team_size: number;
  team_members: Employee[];
  leaves: Leave[];
  total_leaves: number;
  filters_applied: {
    start_date: string | null;
    end_date: string | null;
    status: string;
    employee_id: string | null;
  };
}

interface LeaveBar {
  leave: Leave;
  row: number;
  startCol: number;
  span: number;
  weekIndex: number;
}

const FullCalendarView: React.FC = () => {
    
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [comments, setComments] = useState<string>('');
  const [processingLeaveId, setProcessingLeaveId] = useState<number | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const statusColors: Record<'A' | 'MA' | 'P' | 'D' | 'MD', string> = {
    'A': 'bg-green-100 border-green-500 text-green-800',
    'MA': 'bg-blue-100 border-blue-500 text-blue-800',
    'P': 'bg-yellow-100 border-yellow-500 text-yellow-800',
    'D': 'bg-red-100 border-red-500 text-red-800',
    'MD': 'bg-gray-100 border-gray-500 text-gray-800',
  };

  const barColors: Record<'A' | 'MA' | 'P' | 'D' | 'MD', string> = {
    'A': 'bg-green-400 hover:bg-green-500',
    'MA': 'bg-blue-400 hover:bg-blue-500',
    'P': 'bg-yellow-400 hover:bg-yellow-500',
    'D': 'bg-red-400 hover:bg-red-500',
    'MD': 'bg-gray-400 hover:bg-gray-500',
  };

  // Helper function to parse date strings in local timezone
  const parseLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showModal && modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showModal]);

  const fetchCalendarData = async () => {
    try {
      setIsLoading(true);
      setError('');
       
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1).toISOString().split('T')[0];
      const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

      const response = await getTeamLeaveCalendar({ 
        start_date: startDate, 
        end_date: endDate,
        status: 'A,MA,P' // Include Pending, Approved, and Manager Approved leaves
      });
      setCalendarData(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch calendar data');
    } finally {
      setIsLoading(false);
    }
  };

  const changeMonth = (direction: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const getWeeksInMonth = (): Date[][] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];
    
    // Fill in days before the first day of month
    const startingDayOfWeek = firstDay.getDay();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      currentWeek.push(date);
    }
    
    // Fill in all days of the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      currentWeek.push(date);
      
      if (date.getDay() === 6) { // Saturday, end of week
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    
    // Fill in remaining days of last week
    if (currentWeek.length > 0) {
      const lastDate = currentWeek[currentWeek.length - 1];
      const remainingDays = 6 - lastDate.getDay();
      for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(year, month + 1, i);
        currentWeek.push(date);
      }
      weeks.push(currentWeek);
    }
    
    return weeks;
  };

  const isWeekend = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === currentDate.getMonth();
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && 
           date.getMonth() === today.getMonth() && 
           date.getFullYear() === today.getFullYear();
  };

  const calculateLeaveBars = (week: Date[]): LeaveBar[] => {
    if (!calendarData) return [];
    
    const bars: LeaveBar[] = [];
    const rowAssignments: LeaveBar[][] = []; // Array of arrays to track leaves per row
    
    calendarData.leaves.forEach((leave) => {
      const leaveStart = parseLocalDate(leave.start_date);
      const leaveEnd = parseLocalDate(leave.end_date);
    
      const weekStart = week[0];
      const weekEnd = week[6];
    
      if (leaveEnd < weekStart || leaveStart > weekEnd) {
        return; // No overlap with this week
      }
    
      let startCol = -1;
      let endCol = -1;
    
      for (let i = 0; i < week.length; i++) {
        const day = week[i];
        if (day >= leaveStart && day <= leaveEnd) {
          if (startCol === -1) {
            startCol = i;
          }
          endCol = i;
        }
      }
    
      if (startCol === -1 || endCol === -1) return;
    
      while (startCol <= endCol && isWeekend(week[startCol])) {
        startCol++;
      }
    
      while (endCol >= startCol && isWeekend(week[endCol])) {
        endCol--;
      }
    
      if (startCol > endCol) return;
    
      const span = endCol - startCol + 1;
    
      let row = 0;
      while (true) {
        if (!rowAssignments[row]) {
          rowAssignments[row] = [];
        }
    
        const overlaps = rowAssignments[row].some((assignedLeave) => {
          const assignedStart = assignedLeave.startCol;
          const assignedEnd = assignedLeave.startCol + assignedLeave.span - 1;
          return !(endCol < assignedStart || startCol > assignedEnd);
        });
    
        if (!overlaps) {
          break;
        }
    
        row++;
      }
    
      rowAssignments[row].push({
        leave,
        row,
        startCol,
        span,
        weekIndex: 0,
      });
    
      bars.push({
        leave,
        row,
        startCol,
        span,
        weekIndex: 0,
      });
    });
    
    return bars;
  };

  const handleLeaveClick = (leave: Leave) => {
    setSelectedLeave(leave);
    setComments('');
    setShowModal(true);
  };

  const handleApprove = async (leaveId: number) => {
    setProcessingLeaveId(leaveId);
    const notification = toast.loading("Manager Approving Leave...");

    try {
      await postManagerApproveLeave(leaveId.toString(), comments || '');
      toast.update(notification, {
        render: "Leave Manager Approved successfully",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      // Clear comment
      setComments('');
      // Refresh calendar data
      await fetchCalendarData();
      // Update selected leave
      if (selectedLeave && selectedLeave.id === leaveId) {
        setSelectedLeave({
          ...selectedLeave,
          leave_status: 'MA' as const,
          leave_status_display: 'Manager Approved'
        });
      }
    } catch (error: any) {
      console.log(error);
      let errorMessage = "Failed to approve leave";
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
        if (errorMessage.length > 80) {
          errorMessage = "Failed to approve leave";
        }
      }
      toast.update(notification, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setProcessingLeaveId(null);
    }
  };

  const handleDecline = async (leaveId: number) => {
    setProcessingLeaveId(leaveId);
    const notification = toast.loading("Manager Declining Leave...");

    try {
      await postManagerDeclineLeave(leaveId.toString(), comments || '');
      toast.update(notification, {
        render: "Leave Manager Declined successfully",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      // Clear comment
      setComments('');
      // Refresh calendar data
      await fetchCalendarData();
      // Update selected leave
      if (selectedLeave && selectedLeave.id === leaveId) {
        setSelectedLeave({
          ...selectedLeave,
          leave_status: 'MD' as const,
          leave_status_display: 'Manager Declined'
        });
      }
    } catch (error: any) {
      console.log(error);
      toast.update(notification, {
        render: "Failed to decline Leave",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setProcessingLeaveId(null);
    }
  };

  const handleCommentChange = (value: string) => {
    setComments(value);
  };

//   if (!permissions?.includes('leaves.view_my_employeeleavesdetails')) {
//     return null;
//   }

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-[600px] bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden p-6">
        <div className="text-red-600 dark:text-red-400">Error: {error}</div>
      </div>
    );
  }

  if (!calendarData || calendarData.team_size === 0) {
    return (
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden p-6">
        <div className="text-gray-600 dark:text-gray-400">No team data available</div>
      </div>
    );
  }

  const weeks = getWeeksInMonth();
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <>
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="text-brand-500 dark:text-brand-600" size={24} />
                <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                  Team Leave Calendar
                </h2>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => changeMonth(-1)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronLeft size={20} className="text-gray-600 dark:text-gray-300" />
              </button>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white min-w-[200px] text-center">
                {monthName}
              </h3>
              <button
                onClick={() => changeMonth(1)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ChevronRight size={20} className="text-gray-600 dark:text-gray-300" />
              </button>
            </div>
          </div>

          {/* Calendar */}
          <div className="space-y-1">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
                <div
                  key={day}
                  className="text-center text-sm font-semibold text-gray-600 dark:text-gray-400 py-3 border-b border-gray-200 dark:border-gray-700"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Weeks */}
            {weeks.map((week, weekIndex) => {
              const leaveBars = calculateLeaveBars(week);
              const maxRows = Math.max(3, ...leaveBars.map(b => b.row + 1));
              
              return (
                <div key={weekIndex} className="relative mb-2">
                  {/* Date Row with overlaying leave bars */}
                  <div className="grid grid-cols-7 gap-2" style={{ minHeight: `${80 + maxRows * 32}px` }}>
                    {week.map((date, dayIndex) => {
                      const today = isToday(date);
                      const weekend = isWeekend(date);
                      const currentMonth = isCurrentMonth(date);
                      
                      return (
                        <div
                          key={dayIndex}
                          className={`
                            relative p-3 border rounded-lg
                            ${weekend ? 'bg-gray-50 dark:bg-navy-900' : 'bg-white dark:bg-navy-700'}
                            ${!currentMonth ? 'opacity-40' : ''}
                            ${today ? 'ring-2 ring-blue-500' : 'border-gray-200 dark:border-gray-700'}
                          `}
                        >
                          <div className={`
                            text-sm font-medium
                            ${today ? 'text-blue-600 dark:text-blue-400 font-bold' : ''}
                            ${!currentMonth ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300'}
                          `}>
                            {date.getDate()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Leave Bars - Absolutely positioned over the grid */}
                  <div 
                    className="absolute top-0 left-0 right-0 pointer-events-none"
                    style={{ paddingTop: '44px' }}
                  >
                    <div className="relative grid grid-cols-7 gap-2">
                      {leaveBars.map((bar, barIndex) => (
                        <div
                          key={barIndex}
                          className={`
                            absolute h-7 rounded-md px-2 py-1 cursor-pointer pointer-events-auto
                            ${barColors[bar.leave.leave_status]} 
                            shadow-sm transition-all duration-200
                            flex items-center text-xs text-white font-medium
                            overflow-hidden
                          `}
                          style={{
                            left: `calc(${(bar.startCol / 7) * 100}% + ${bar.startCol * 0.25}rem)`,
                            width: `calc(${(bar.span / 7) * 100}% - ${0.5}rem)`,
                            top: `${bar.row * 32}px`,
                            zIndex: 10,
                          }}
                          onClick={() => handleLeaveClick(bar.leave)}
                          title={`${bar.leave.employee.full_name} - ${bar.leave.leave_type.description}`}
                        >
                          <span className="truncate">
                            {bar.leave.employee.full_name} - {bar.leave.leave_type.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-400 rounded"></div>
                <span className="text-gray-600 dark:text-gray-400">Approved</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-400 rounded"></div>
                <span className="text-gray-600 dark:text-gray-400">Manager Approved</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-400 rounded"></div>
                <span className="text-gray-600 dark:text-gray-400">Pending</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Leave Details with Blur Backdrop */}
      {showModal && selectedLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div ref={modalRef} className="bg-white dark:bg-navy-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                  Leave Details
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-600 dark:text-gray-300" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-6">
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start gap-4">
                  {selectedLeave.employee.photo ? (
                    <img
                      src={selectedLeave.employee.photo}
                      alt={selectedLeave.employee.full_name}
                      className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 dark:text-blue-400 font-semibold text-lg">
                        {selectedLeave.employee.first_name[0]}{selectedLeave.employee.last_name[0]}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-3 gap-2">
                      <div className="min-w-0">
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-white truncate">
                          {selectedLeave.employee.full_name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {selectedLeave.employee.designation}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${statusColors[selectedLeave.leave_status]}`}>
                        {selectedLeave.leave_status_display}
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400 block mb-1">Leave Type:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {selectedLeave.leave_type.description}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400 block mb-1">Duration:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {selectedLeave.working_days} day{selectedLeave.working_days !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="col-span-1 sm:col-span-2">
                          <span className="text-gray-500 dark:text-gray-400 block mb-1">Period:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {parseLocalDate(selectedLeave.start_date).toLocaleDateString('en-US', { 
                              weekday: 'short', 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })} - {parseLocalDate(selectedLeave.end_date).toLocaleDateString('en-US', { 
                              weekday: 'short', 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </span>
                        </div>
                      </div>

                      {selectedLeave.reason && (
                        <div className="text-sm pt-3 border-t border-gray-200 dark:border-gray-700">
                          <span className="text-gray-500 dark:text-gray-400 block mb-1">Reason:</span>
                          <p className="text-gray-700 dark:text-gray-300">{selectedLeave.reason}</p>
                        </div>
                      )}

                      {selectedLeave.comments && (
                        <div className="text-sm">
                          <span className="text-gray-500 dark:text-gray-400 block mb-1">Comments:</span>
                          <p className="text-gray-700 dark:text-gray-300">{selectedLeave.comments}</p>
                        </div>
                      )}

                      {/* Approve/Decline buttons for pending requests */}
                      {selectedLeave.leave_status === 'P' && (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Comments (optional)
                              </label>
                              <textarea
                                value={comments}
                                onChange={(e) => handleCommentChange(e.target.value)}
                                placeholder="Enter comments here..."
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-navy-700 text-gray-900 dark:text-white text-sm resize-none"
                                rows={2}
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApprove(selectedLeave.id)}
                                disabled={processingLeaveId === selectedLeave.id}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-colors text-sm font-medium disabled:cursor-not-allowed"
                              >
                                <Check size={16} />
                                {processingLeaveId === selectedLeave.id ? 'Processing...' : 'Approve'}
                              </button>
                              <button
                                onClick={() => handleDecline(selectedLeave.id)}
                                disabled={processingLeaveId === selectedLeave.id}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors text-sm font-medium disabled:cursor-not-allowed"
                              >
                                <XCircle size={16} />
                                {processingLeaveId === selectedLeave.id ? 'Processing...' : 'Decline'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default FullCalendarView;